import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, NavLink, useMatch, useSearchParams } from "react-router-dom";
import { Download, Loader2, Mic } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useUserAgents } from "@/hooks/agent";
import { useBrandTestRuns } from "@/hooks/agent/useBrandIntegrity";
import { useBrandIntegrityTestTracking } from "@/hooks/agent/useBrandIntegrityTestTracking";
import { cn } from "@/lib/utils";
import { Agent } from "@/types/agent/agent";
import { PhoneCheckKind } from "@/types/agent/brand-integrity";
import { isPhoneCheckKind, PHONE_CHECK_RUN_TYPES, PHONE_TESTS_PATH } from "@/utils/agent/phoneCheck";
import { BrandTestFilters } from "./components/BrandTestFilters";
import { BrandTestList } from "./components/BrandTestList";
import { RunTestModal } from "./components/RunTestModal";
import { BrandQuickTestModal } from "./components/BrandQuickTestModal";
import { ExportOptionsSheet } from "./components/ExportOptionsSheet";
import { FullAuditSheet } from "./components/FullAuditSheet";
import { PhoneCheckSheet } from "./components/PhoneCheckSheet";
import { PhoneTestsPanel } from "./components/PhoneTestsPanel";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const TABS = [
  { to: "/genie-hub/brand-integrity", end: true, label: "Audits" },
  { to: PHONE_TESTS_PATH, end: false, label: "Phone Tests" },
];

const PHONE_CHECK_RUN_TYPE_LIST = Object.values(PHONE_CHECK_RUN_TYPES);
const GENIE_LIST_LIMIT = 100;

export default function BrandIntegrityPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const isPhoneTests = !!useMatch(PHONE_TESTS_PATH);
  // Full Genie records (not the light picker list): the Phone Tests tab reads
  // each Genie's phone numbers and handoff settings from them.
  const { data: agents, isLoading: agentsLoading } = useUserAgents(GENIE_LIST_LIMIT);
  const { isAnyTestRunning, addRunningTest, runningTests } = useBrandIntegrityTestTracking();
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<"all" | "passed" | "failed">("all");
  const [dateRange, setDateRange] = useState<string>("30d");
  const [showRunTestModal, setShowRunTestModal] = useState(false);
  const [showQuickTestModal, setShowQuickTestModal] = useState(false);
  const [showFullAuditSheet, setShowFullAuditSheet] = useState(false);
  const [auditAgent, setAuditAgent] = useState<Agent | null>(null);
  const [phoneCheck, setPhoneCheck] = useState<{ kind: PhoneCheckKind; agent: Agent } | null>(null);
  const [phoneCheckOpen, setPhoneCheckOpen] = useState(false);
  const [showExportSheet, setShowExportSheet] = useState(false);
  const [showExportHint, setShowExportHint] = useState(false);
  const [showFullAuditHint, setShowFullAuditHint] = useState(false);
  const [expandedTestRunId, setExpandedTestRunId] = useState<string | null>(null);
  const pageSize = 20;

  const { data: testRunsData, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading: testRunsLoading } = useBrandTestRuns({
    pageSize,
    agentId: selectedAgent || undefined,
    statusFilter,
    dateRange,
    ...(isPhoneTests ? { runTypes: PHONE_CHECK_RUN_TYPE_LIST } : { excludeRunTypes: PHONE_CHECK_RUN_TYPE_LIST }),
    enabled: !agentsLoading && (agents?.length || 0) > 0,
    agents: agents || [],
  });

  const testRuns = testRunsData?.pages.flatMap((page) => page.data) || [];
  const totalCount = testRunsData?.pages[0]?.totalCount || 0;

  const invalidateQueries = () => {
    queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === "brand-test-runs-infinite" || query.queryKey[0] === "brand-integrity-stats",
    });
  };

  const refetchTestRuns = async () => {
    // Invalidate all brand-test-runs-infinite queries regardless of filters
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === "brand-test-runs-infinite",
    });
    // Wait a tick to ensure state updates
    await new Promise(resolve => setTimeout(resolve, 100));
    // Then explicitly refetch all matching queries
    await queryClient.refetchQueries({
      predicate: (query) => query.queryKey[0] === "brand-test-runs-infinite",
    });
  };

  const handleFilterChange = (filterType: "agent" | "status" | "date", value: any) => {
    if (filterType === "agent") {
      setSelectedAgent(value);
    } else if (filterType === "status") {
      setStatusFilter(value);
    } else if (filterType === "date") {
      setDateRange(value);
    }
  };

  const handleExport = () => {
    if (!selectedAgent) {
      return;
    }
    setShowExportSheet(true);
  };

  const getSelectedAgent = () => {
    return agents?.find((a) => a.id === selectedAgent);
  };

  const handleOpenFullAudit = () => {
    const target = getSelectedAgent();
    if (!target) {
      setShowFullAuditHint(true);
      return;
    }
    setAuditAgent(target);
    setShowFullAuditSheet(true);
  };

  const openPhoneCheck = (kind: PhoneCheckKind, agent: Agent) => {
    setPhoneCheck({ kind, agent });
    setPhoneCheckOpen(true);
  };

  // Deep link from the phone panel's Test buttons: ?agent=<id>&check=handoff|transfer
  const linkedAgentId = searchParams.get("agent");
  const linkedCheck = searchParams.get("check");
  useEffect(() => {
    if (!isPhoneCheckKind(linkedCheck) || !linkedAgentId || !agents) return;

    const target = agents.find((a) => a.id === linkedAgentId);
    if (target) {
      setSelectedAgent(target.id);
      setPhoneCheck({ kind: linkedCheck, agent: target });
      setPhoneCheckOpen(true);
    }
    setSearchParams({}, { replace: true });
  }, [linkedAgentId, linkedCheck, agents, setSearchParams]);

  const trackStartedRun = (testRunId: number, agentName?: string) => {
    addRunningTest({ testRunId: String(testRunId), agentName: agentName ?? "Genie", totalTests: 0 });
    setExpandedTestRunId(String(testRunId));
    refetchTestRuns();
  };

  const showRunResults = (testRunId: number) => {
    setExpandedTestRunId(String(testRunId));
    refetchTestRuns();
  };

  return (
    <div className="min-h-screen bg-[#F9FAFC]">
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8 max-w-[1920px] w-full">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 md:mb-8">
          <div className="flex items-center gap-3 md:gap-4 min-w-0">
            <GenieIcon name="trending-up" size={28} className="h-7 w-7 md:h-8 md:w-8 flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-xl md:text-3xl font-bold text-[#222B45] leading-tight truncate">
                Genie Trust Center
              </h1>
              <p className="text-sm md:text-base text-[#64748B] mt-0.5 hidden sm:block">
                Stress-test your Genie's behaviour across every conversation it might face.
              </p>
              <Link
                to="/genie-hub/brand-integrity/preview"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-genie-primary hover:underline mt-1.5"
              >
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] animate-pulse" />
                Preview new live audit experience
              </Link>
            </div>
          </div>

          {!isPhoneTests && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            {/* PRIMARY: Run Full Brand Audit */}
            <div
              className="relative"
              onMouseEnter={() => !selectedAgent && setShowFullAuditHint(true)}
              onMouseLeave={() => setShowFullAuditHint(false)}
            >
              <Button
                onClick={handleOpenFullAudit}
                disabled={!selectedAgent}
                className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-5 h-10 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed"
              >
                Run Full Audit
              </Button>
              {!selectedAgent && showFullAuditHint && (
                <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1 text-xs text-gray-500 whitespace-nowrap bg-white px-2 py-1 rounded border shadow-sm z-10">
                  Select a genie first
                </span>
              )}
            </div>

            {/* SECONDARY: smaller utility actions */}
            <div className="flex items-center gap-2 sm:gap-3">
              <Button
                variant="outline"
                onClick={() => setShowRunTestModal(true)}
                className="h-10"
              >
                Custom Test
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowQuickTestModal(true)}
                className="h-10"
              >
                <Mic className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Manual</span>
                <span className="sm:hidden">Manual</span>
              </Button>
              <div className="relative">
                <Button
                  variant="outline"
                  onClick={selectedAgent ? handleExport : undefined}
                  onMouseEnter={() => !selectedAgent && setShowExportHint(true)}
                  onMouseLeave={() => setShowExportHint(false)}
                  disabled={!selectedAgent}
                  className="h-10"
                >
                  <Download className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Export</span>
                </Button>
                {!selectedAgent && showExportHint && (
                  <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1 text-xs text-gray-500 whitespace-nowrap bg-white px-2 py-1 rounded border shadow-sm z-10">
                    Select a genie first
                  </span>
                )}
              </div>
            </div>
          </div>
          )}
        </div>

        <nav className="flex gap-1 border-b border-genie-border mb-6">
          {TABS.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end}
              className={({ isActive }) =>
                cn(
                  "border-b-2 px-4 py-2.5 min-h-[44px] text-sm font-semibold transition-colors",
                  isActive
                    ? "border-genie-primary text-genie-primary"
                    : "border-transparent text-genie-muted hover:text-genie-text",
                )
              }
            >
              {tab.label}
            </NavLink>
          ))}
        </nav>

        {agentsLoading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-genie-primary mb-4"></div>
            <p className="text-gray-600">Loading your genies...</p>
          </div>
        ) : (agents || []).length === 0 ? (
          <div className="text-center py-16">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">No Genies Found</h2>
            <p className="text-gray-600 mb-6">You need to create a genie first to run brand integrity tests.</p>
            <a href="/genie-hub/my-genies" className="inline-flex items-center px-4 py-2 bg-genie-primary text-white rounded-lg hover:bg-genie-primary-hover transition">
              Go to My Genies
            </a>
          </div>
        ) : (
          <>
            {/* Filters */}
            <BrandTestFilters
              agents={agents || []}
              selectedAgent={selectedAgent}
              onSelectedAgentChange={(agentId) => handleFilterChange("agent", agentId)}
              statusFilter={statusFilter}
              onStatusFilterChange={(filter) => handleFilterChange("status", filter)}
              dateRange={dateRange}
              onDateRangeChange={(range) => handleFilterChange("date", range)}
            />

            {isPhoneTests && <PhoneTestsPanel agent={getSelectedAgent() ?? null} onRunCheck={openPhoneCheck} />}

            {/* Test Results List */}
            <BrandTestList
              testRuns={testRuns}
              agents={agents || []}
              isLoading={testRunsLoading}
              invalidateQueries={invalidateQueries}
              expandedRunId={expandedTestRunId}
              onExpandedRunChange={setExpandedTestRunId}
              runningTests={runningTests}
            />

            {/* Infinite scroll loading */}
            {hasNextPage && (
              <div
                ref={(el) => {
                  if (el && !testRunsLoading && !isFetchingNextPage) {
                    const observer = new IntersectionObserver(
                      (entries) => {
                        if (entries[0].isIntersecting) {
                          fetchNextPage();
                        }
                      },
                      { threshold: 0.1 }
                    );
                    observer.observe(el);
                  }
                }}
                className="flex justify-center py-8"
              >
                {isFetchingNextPage && (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Loading more tests...</span>
                  </div>
                )}
              </div>
            )}

            {/* Total count */}
            {testRuns.length > 0 && (
              <div className="text-center py-4 text-sm text-gray-500">
                Showing {testRuns.length} of {totalCount} test runs
              </div>
            )}

            {isPhoneTests && !testRunsLoading && testRuns.length === 0 && (
              <p className="text-center py-8 text-sm text-genie-muted">
                No phone tests yet. Run a check above and the result will appear here.
              </p>
            )}

            {/* Empty state */}
            {!isPhoneTests && !testRunsLoading && testRuns.length === 0 && (
              <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
                <div className="rounded-[14px] bg-white p-10 md:p-12 text-center">
                  <div className="mx-auto mb-5 h-16 w-16 rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] flex items-center justify-center">
                    <GenieIcon name="clipboard-check" size={36} className="h-9 w-9" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-[#222B45] mb-2">
                    No audits yet
                  </h3>
                  <p className="text-sm md:text-base text-[#64748B] mb-6 max-w-md mx-auto leading-relaxed">
                    {selectedAgent
                      ? "Run a Full Audit on this Genie and we'll tailor around 120 scenarios across every behaviour that matters."
                      : "Pick a Genie above, then run a Full Audit — we'll tailor around 120 scenarios across every behaviour that matters."}
                  </p>
                  <Button
                    onClick={handleOpenFullAudit}
                    disabled={!selectedAgent}
                    className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed"
                  >
                    Run Full Audit
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Run Test Modal */}
      <RunTestModal
        isOpen={showRunTestModal}
        onClose={() => setShowRunTestModal(false)}
        agents={agents || []}
        preSelectedAgentId={selectedAgent}
        isTestRunning={isAnyTestRunning}
        onTestComplete={() => {
          setShowRunTestModal(false);
          invalidateQueries();
        }}
        onTestStarted={(testRunId, agentName, totalTests) => {
          addRunningTest({
            testRunId,
            agentName,
            totalTests,
          });
          setExpandedTestRunId(testRunId);
          refetchTestRuns();
        }}
      />

      {/* Quick Test Modal */}
      <BrandQuickTestModal
        isOpen={showQuickTestModal}
        onClose={() => setShowQuickTestModal(false)}
        agents={agents || []}
        preSelectedAgentId={selectedAgent}
        onTestSaved={() => {
          invalidateQueries();
        }}
      />

      {/* Export Options Sheet */}
      {selectedAgent && getSelectedAgent() && (
        <ExportOptionsSheet
          isOpen={showExportSheet}
          onClose={() => setShowExportSheet(false)}
          agent={getSelectedAgent()!}
          dateRange={dateRange}
        />
      )}

      {/* Full Brand Audit Sheet */}
      <FullAuditSheet
        isOpen={showFullAuditSheet}
        onClose={() => setShowFullAuditSheet(false)}
        agent={auditAgent}
        onAuditStarted={(testRunId) => trackStartedRun(testRunId, auditAgent?.name)}
        onViewResults={showRunResults}
      />

      {phoneCheck && (
        <PhoneCheckSheet
          isOpen={phoneCheckOpen}
          onClose={() => setPhoneCheckOpen(false)}
          kind={phoneCheck.kind}
          agent={phoneCheck.agent}
          onCheckStarted={(testRunId) => trackStartedRun(testRunId, phoneCheck.agent.name)}
          onViewResults={showRunResults}
        />
      )}
    </div>
  );
}
